import type { ListQuery, OfferQuery, ListQueryVariables } from '~/lib/goliath/goliath.types';

export const isListPageIndexed = (
  variables: ListQueryVariables | undefined,
  offer: ListQuery['offers']
) => {
  if ((offer?.variantsCount || 0) === 0) return false; // empty response
  if ((offer?.pagination?.page || 0) !== 1) return false; // we want only the 1. page to be indexed
  if (variables?.search || variables?.complexSearch) return false; // custom filters

  const filter = variables?.filter || {};

  // The top level categories should not be indexed. The Sub-Categories contain all products.
  if (
    filter.productCategoryPaths?.find((path) =>
      ['smartphones-und-handys', 'tablets-und-zubehoer', 'tv'].includes(path)
    )
  ) {
    return false;
  }

  // Define which keys are allowed to be indexed.
  const expectedKeys = new Set([
    'brandSlugs',
    'colorSlugs',
    'productCategoryPaths',
    'listed',
    'productAvailable',
    'hasAlwaysDisplayed',
  ]);
  const unexpectedKeys = Object.keys(filter).find((key) => !expectedKeys.has(key));

  if (unexpectedKeys) return false;

  const multipleFiltersActive = [
    filter.brandSlugs,
    filter.colorSlugs,
    filter.productCategoryPaths,
  ].some((entry) => (entry?.length || 0) > 1);

  if (multipleFiltersActive) return false;

  const hasActiveBrandFilter = (filter.brandSlugs?.length || 0) > 0;
  const hasActiveCategoryFilter =
    (filter.productCategoryPaths?.find((category) => category.includes('/'))?.length || 0) > 0;
  const hasActiveColorFilter = (filter.colorSlugs?.length || 0) > 0;

  if (hasActiveBrandFilter && hasActiveCategoryFilter && hasActiveColorFilter) return false;

  // Special case for Wearables: Index page, but no filters
  if (
    filter.productCategoryPaths?.find((path) => path === 'wearables') &&
    (hasActiveBrandFilter || hasActiveColorFilter)
  ) {
    return false;
  }

  return true;
};

export const isProductPageIndexed = (offer: OfferQuery['offerBySlugs']) => {
  if (!offer?.product?.availability) return false;

  const primaryCategoryPath = offer.product.family?.primaryCategory?.path;
  // we do not want to list smartphones & tables from telekom. They are in the telekom store.
  if (
    primaryCategoryPath &&
    ['smartphones-und-handys/smartphones', 'tablets-und-zubehoer/tablets'].includes(
      primaryCategoryPath
    ) &&
    offer.product.availability.provider === 'TELEKOM'
  ) {
    return false;
  }
  return true;
};
